import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { navHrefs } from "../data/staticData";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageSwitcher } from "./LanguageSwitcher";

const LOGO_URL = "/assets/logo.png";

export function Navbar({ onOpenDemo }) {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navLabels = [t.nav.features, t.nav.howItWorks, t.nav.reports, t.nav.useCases, t.nav.pricing, t.nav.faq];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass-nav border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <a href="#hero" className="flex items-center gap-2 group" data-testid="navbar-logo">
          <img src={LOGO_URL} alt="NeuroScan" className="w-12 h-12 logo-neon" />
          <span className="font-display font-bold text-lg tracking-tighter text-white">
            NeuroScan
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {navHrefs.map((href, i) => (
            <a
              key={href}
              href={href}
              data-testid={`navbar-link-${href.replace("#", "")}`}
              className="text-sm font-medium text-zinc-400 hover:text-white transition-colors duration-200"
            >
              {navLabels[i]}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          <LanguageSwitcher />
          <button
            onClick={onOpenDemo}
            data-testid="navbar-cta-button"
            className="inline-flex items-center px-5 py-2.5 rounded-md bg-[#00F0FF] text-black text-sm font-semibold hover:brightness-110 hover:scale-[1.03] transition-all duration-200"
          >
            {t.site.ctaPrimary}
          </button>
        </div>

        <button
          className="lg:hidden text-white"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="navbar-mobile-toggle"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden glass-nav border-t border-white/10 px-6 py-6 flex flex-col gap-5" data-testid="navbar-mobile-menu">
          {navHrefs.map((href, i) => (
            <a
              key={href}
              href={href}
              onClick={() => setMobileOpen(false)}
              className="text-sm font-medium text-zinc-300"
            >
              {navLabels[i]}
            </a>
          ))}
          <div className="pt-2 border-t border-white/10">
            <LanguageSwitcher mobile />
          </div>
          <button
            onClick={() => {
              setMobileOpen(false);
              onOpenDemo();
            }}
            data-testid="navbar-mobile-cta-button"
            className="mt-2 px-5 py-2.5 rounded-md bg-[#00F0FF] text-black text-sm font-semibold"
          >
            {t.site.ctaPrimary}
          </button>
        </div>
      )}
    </motion.header>
  );
}
